import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';
import SeverityChip from './SeverityChip';

type ResolvedFilter = 'open' | 'resolved' | 'all';

const truncate = (s: string | null | undefined, n = 120) => {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
};

const ErrorsTab: React.FC = () => {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<ResolvedFilter>('open');
  const [days, setDays] = useState<number>(7);
  const [page, setPage] = useState(1);
  const perPage = 50;

  const [resolveRunId, setResolveRunId] = useState<number | null>(null);
  const [resolveNote, setResolveNote] = useState<string>('');
  const [lastResult, setLastResult] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['admin-errors', filter, days, page],
    queryFn: () =>
      adminAPI.getErrors({
        resolved: filter,
        days,
        page,
        per_page: perPage,
      }),
    staleTime: 15000,
  });

  const errors = data?.errors || [];
  const totalPages = data?.pages || 1;

  const resolveMut = useMutation({
    mutationFn: ({ runId, note }: { runId: number; note?: string }) =>
      adminAPI.resolveError(runId, note),
    onSuccess: (resp) => {
      setLastResult(`Resolved run #${resp.run_id}`);
      qc.invalidateQueries({ queryKey: ['admin-errors'] });
      qc.invalidateQueries({ queryKey: ['admin-actions'] });
    },
    onError: (err: any) => {
      setLastResult(`Error: ${err?.response?.data?.detail || err?.message || 'unknown'}`);
    },
  });

  const unresolveMut = useMutation({
    mutationFn: (runId: number) => adminAPI.unresolveError(runId),
    onSuccess: (resp) => {
      setLastResult(`Unresolved run #${resp.run_id}`);
      qc.invalidateQueries({ queryKey: ['admin-errors'] });
      qc.invalidateQueries({ queryKey: ['admin-actions'] });
    },
    onError: (err: any) => {
      setLastResult(`Error: ${err?.response?.data?.detail || err?.message || 'unknown'}`);
    },
  });

  const openResolveDialog = (runId: number) => {
    setResolveRunId(runId);
    setResolveNote('');
  };

  const submitResolve = () => {
    if (resolveRunId != null) {
      resolveMut.mutate({ runId: resolveRunId, note: resolveNote || undefined });
    }
    setResolveRunId(null);
    setResolveNote('');
  };

  return (
    <Panel label="ETL ERRORS">
      {lastResult && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 2, color: lastResult.startsWith('Error') ? '#ff3344' : '#00ff41' }}
        >
          {lastResult}
        </Typography>
      )}
      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          value={filter}
          exclusive
          size="small"
          onChange={(_, v) => {
            if (v) {
              setFilter(v);
              setPage(1);
            }
          }}
        >
          <ToggleButton value="open">OPEN</ToggleButton>
          <ToggleButton value="resolved">RESOLVED</ToggleButton>
          <ToggleButton value="all">ALL</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          label="Days"
          type="number"
          value={days}
          onChange={(e) => {
            const n = Math.max(1, Math.min(365, parseInt(e.target.value || '7', 10)));
            setDays(n);
            setPage(1);
          }}
          sx={{ width: 120 }}
          inputProps={{ min: 1, max: 365 }}
        />
        <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
          {data?.total ?? 0} errors match
        </Typography>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>STARTED</TableCell>
              <TableCell>JOB</TableCell>
              <TableCell align="right">RECORDS</TableCell>
              <TableCell>ERROR</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="caption" color="textSecondary">
                    No errors match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              errors.map((e: any) => {
                const resolved = !!e.resolved_at;
                return (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {e.started_at ? new Date(e.started_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>{e.job_name}</TableCell>
                    <TableCell align="right">{(e.records_processed ?? 0).toLocaleString()}</TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Tooltip title={e.error_message || ''} placement="top-start" arrow>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            maxWidth: 420,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'bottom',
                          }}
                        >
                          {truncate(e.error_message, 160)}
                        </Box>
                      </Tooltip>
                      {resolved && e.resolution_note && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          note: {e.resolution_note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {resolved ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <SeverityChip severity="OK" />
                          <Typography variant="caption" color="textSecondary">
                            {e.resolved_by || '—'}
                            {e.resolved_at ? ` · ${new Date(e.resolved_at).toLocaleString()}` : ''}
                          </Typography>
                        </Box>
                      ) : (
                        <SeverityChip severity="CRITICAL" />
                      )}
                    </TableCell>
                    <TableCell>
                      {resolved ? (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={unresolveMut.isPending}
                          onClick={() => {
                            if (window.confirm(`Unresolve run #${e.id}?`)) {
                              unresolveMut.mutate(e.id);
                            }
                          }}
                        >
                          [ UNRESOLVE ]
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={resolveMut.isPending}
                          onClick={() => openResolveDialog(e.id)}
                        >
                          [ RESOLVE ]
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            size="small"
          />
        </Box>
      )}

      <Dialog open={resolveRunId != null} onClose={() => setResolveRunId(null)} fullWidth maxWidth="sm">
        <DialogTitle>Resolve run #{resolveRunId}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Mark this error as resolved. An optional note will be stored with the resolution.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            label="Resolution note (optional)"
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveRunId(null)}>[ CANCEL ]</Button>
          <Button onClick={submitResolve} variant="outlined" color="warning">
            [ CONFIRM RESOLVE ]
          </Button>
        </DialogActions>
      </Dialog>
    </Panel>
  );
};

export default ErrorsTab;
